import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@app/environment/environment';
import {
  AcceptedInvestmentResponse,
  Investment,
} from '@app/models/investment.models';
import { Project, ProjectPublicResponse } from '@app/models/project.models';
import {
  PaginatedData,
  PaginatedDataRequest,
  ProjectDataRequest,
} from '@app/models/shared.models';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProjectService {
  private readonly API_URL = environment.apiUrl;

  constructor(private httpClient: HttpClient) {}

  getActualProjects(
    request: ProjectDataRequest,
  ): Observable<PaginatedData<Project>> {
    return this.projectsParams(request, '/Projects/Active');
  }

  getActiveProjectsLimit(
    take: number,
  ): Observable<PaginatedData<Project>> {
    return this.getActualProjects({ skip: 0, take });
  }

  private projectsParams(request: ProjectDataRequest, path: string) {
    let params = new HttpParams()
      .set('skip', String(request.skip ?? 0))
      .set('take', String(request.take ?? 10));
    if (request.sort?.length) {
      params = params.set('sort', JSON.stringify(request.sort));
    }
    if (request.filter?.trim()) {
      params = params.set('filter', request.filter.trim());
    }
    if (request.currency?.length) {
      params = params.set('Currency', request.currency.join(','));
    }
    if (request.country?.length) {
      params = params.set('Country', request.country.join(','));
    }
    if (request.category?.length) {
      const category = request.category.join(',');
      params = params.set('Category', category).set('category', category);
    }
    if (request.interest?.trim()) {
      params = params.set('Interest', request.interest.trim());
    }
    if (request.yield?.trim()) {
      params = params.set('Yield', request.yield.trim());
    }
    if (request.duration?.trim()) {
      params = params.set('Duration', request.duration.trim());
    }
    return this.httpClient.get<PaginatedData<Project>>(
      `${this.API_URL}${path}`,
      { params },
    );
  }

  getPreparedProjects(
    request: ProjectDataRequest,
  ): Observable<PaginatedData<Project>> {
    return this.projectsParams(request, '/Projects/Prepared');
  }

  getClosedProjects(
    request: ProjectDataRequest,
  ): Observable<PaginatedData<Project>> {
    return this.projectsParams(request, '/Projects/Closed');
  }

  getProjectById(projectId: string): Observable<ProjectPublicResponse> {
    return this.httpClient.get<ProjectPublicResponse>(
      `${this.API_URL}/Project/${projectId}`,
    );
  }

  updateProjectStateToPublished(projectId: string): Observable<void> {
    return this.httpClient.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/Published`,
      {},
    );
  }

  updateProjectStateToStaffAnalyzeStarted(projectId: string): Observable<void> {
    return this.httpClient.put<void>(
      `${this.API_URL}/ProjectState/${projectId}/StaffAnalyzeStarted`,
      {},
    );
  }

  getOwnProjects(
    request: PaginatedDataRequest,
  ): Observable<PaginatedData<Project>> {
    return this.httpClient.get<PaginatedData<Project>>(
      `${this.API_URL}/Project/Own?skip=${request.skip}&take=${request.take}`,
    );
  }

  getOwnInvestedProjects(
    request: PaginatedDataRequest,
  ): Observable<PaginatedData<Project>> {
    return this.httpClient.get<PaginatedData<Project>>(
      `${this.API_URL}/Project/Own/Investments?skip=${request.skip}&take=${request.take}`,
    );
  }

  getInvestments(
    request: PaginatedDataRequest,
  ): Observable<PaginatedData<Investment>> {
    const params = new HttpParams()
      .set('skip', String(request.skip ?? 0))
      .set('take', String(request.take ?? 10));
    return this.httpClient.get<PaginatedData<Investment>>(
      `${this.API_URL}/Investments`,
      { params },
    );
  }

  getAcceptedInvestments(
    request: PaginatedDataRequest,
  ): Observable<PaginatedData<AcceptedInvestmentResponse>> {
    const params = new HttpParams()
      .set('skip', String(request.skip ?? 0))
      .set('take', String(request.take ?? 10));
    return this.httpClient.get<PaginatedData<AcceptedInvestmentResponse>>(
      `${this.API_URL}/Investments/List/Accepted`,
      { params },
    );
  }

  getPendingInvestments(
    request: PaginatedDataRequest,
  ): Observable<PaginatedData<AcceptedInvestmentResponse>> {
    const params = new HttpParams()
      .set('skip', String(request.skip ?? 0))
      .set('take', String(request.take ?? 10));
    return this.httpClient.get<PaginatedData<AcceptedInvestmentResponse>>(
      `${this.API_URL}/Investments/List/Pending`,
      { params },
    );
  }

  getFollowedProjects(
    request: PaginatedDataRequest,
  ): Observable<PaginatedData<Project>> {
    return this.httpClient.get<PaginatedData<Project>>(
      `${this.API_URL}/Project/Own/Followed?skip=${request.skip}&take=${request.take}`,
    );
  }

  getAllFollowedProjects(): Observable<Project[]> {
    return this.httpClient
      .get<
        PaginatedData<Project>
      >(`${this.API_URL}/Project/Own/Followed?skip=0&take=10000`)
      .pipe(map((response) => response.data));
  }

  follow(projectId: string): Observable<any> {
    return this.httpClient.put<any>(
      `${this.API_URL}/Project/${projectId}/Follow`,
      null,
    );
  }

  unFollow(projectId: string): Observable<any> {
    return this.httpClient.put<any>(
      `${this.API_URL}/Project/${projectId}/UnFollow`,
      null,
    );
  }

  invest(projectId: string, amount: number, investmentProtection: boolean) {
    return this.httpClient.post<any>(`${this.API_URL}/Investments`, {
      projectId,
      amount,
      comment: '',
      investmentProtection,
    });
  }

  cancelInvestment(investmentId: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.API_URL}/Investments/${investmentId}/Cancel`,
    );
  }
}
